import SoqlDataProvider from 'common/visualizations/dataProviders/SoqlDataProvider';
import type { View } from 'common/types/view';
import SoqlHelpers from 'common/visualizations/helpers/SoqlHelpers';
import { Vif } from 'common/visualizations/vif';
import _ from 'lodash';
import { FeatureFlags } from 'common/feature_flags';
import { ClientContextVariable } from 'common/types/clientContextVariable';
import { migrateVif } from 'common/visualizations/helpers/migrateVif';

export interface ExportStateFunctions {
  setRowCountFiltered: (count: number) => void;
}

export const filterRowData = (
  view: View,
  exportFunctions: ExportStateFunctions,
  queryString: string,
  clientContextVariables: ClientContextVariable[]
) => {
  const domain = window.location.hostname;
  const datasetUid = view.id;
  if (domain && datasetUid) {
    const soqlDataProvider = new SoqlDataProvider({ domain, datasetUid, clientContextVariables }, true);
    soqlDataProvider.getRowCountForQuery(queryString).then((resp) => {
      exportFunctions.setRowCountFiltered(resp);
    });
  }
};

export const getTotalRowCount = (
  view: View,
  setRowCountAll: (count: number) => void,
  clientContextVariables: ClientContextVariable[]
) => {
  const domain = window.location.hostname;
  const datasetUid = view.id;
  if (domain && datasetUid) {
    const soqlDataProvider = new SoqlDataProvider({ domain, datasetUid, clientContextVariables }, true);
    soqlDataProvider.getRowCount('').then(function (resp) {
      setRowCountAll(resp);
    });
  }
};

// This will only work with a vif from an AG-TABLE otherwise will return a *
const getSelectFromVif = (vif: Vif): string => {
  const type = vif.series[0]?.type;
  const visibleColumns = vif.series[0]?.dataSource.dimension?.columns || [];

  if (type === 'agTable') {
    const selectedColumns = visibleColumns.map((column: any) => {
      if (!column.hide && !(column.flags || []).includes('hidden')) {
        return column.fieldName;
      }
    });
    const columnClause = _.compact(selectedColumns).join(', ');
    return _.isEmpty(columnClause) ? '*' : columnClause;
  }
  return '*';
};

const getWhereFromVif = (vif: Vif) => {
  const filters = vif.series[0]?.dataSource?.filters || [];
  return _.compact(filters.map(SoqlHelpers.filterToWhereClauseComponent)).join(' AND ');
};

const convertAscending = (ascending: boolean) => (ascending ? 'ASC' : 'DESC');

export const getOrderByFromVif = (vif: Vif) => {
  const order = vif?.configuration?.order || [];
  // The vif seems to add :id when there is no sort.
  // This is problematic because its not an order by that a user has added,
  // so removing it from the array here if present.
  _.remove(order, (v) => v.columnName === ':id');

  if (!order || !order.length) return;

  const orderString = order.map((v) => `${v.columnName} ${convertAscending(!!v.ascending)}`).join(', ');

  return orderString;
};

const getSearchFromVif = (vif: Vif) => {
  // search string is not on the vif type but its there
  // @ts-expect-error TS(2339) FIXME: Property 'searchString' does not exist on type 'Da... Remove this comment to see the full error message
  const search = vif.series[0]?.dataSource?.searchString;
  // we want to return undefined and not an empty string when there is no search
  if (search) return search;
  return;
};

export const getQueryStringFromVif = (vif: Vif) => {
  const cleanVif = migrateVif(vif);
  const selectClause = getSelectFromVif(cleanVif);
  const whereClause = getWhereFromVif(cleanVif);
  const orderBy = getOrderByFromVif(cleanVif);
  const searchClause = getSearchFromVif(cleanVif);

  let queryString = `SELECT ${selectClause}`;
  if (whereClause) queryString += ` WHERE ${whereClause}`;
  if (orderBy) queryString += ` ORDER BY ${orderBy}`;
  if (searchClause) queryString += ` SEARCH "${searchClause}"`;
  return queryString;
};

const getNonSoqlOrderBy = (view: View) => {
  // this is where order bys are stored on default views
  const order = view.metadata?.jsonQuery?.order;
  if (!order || !order.length) return;

  const orderString = order
    .map((v: any) => `${v.columnFieldName} ${convertAscending(!!v.ascending)}`)
    .join(', ');
  return ` ORDER BY ${orderString}`;
};

// a basic select of all the non-hidden columns on the view
// plus an order by if there is one stored in the jsonQuery object (default views only)
export const getQueryStringFromView = (view: View) => {
  let columns = view.columns;
  let queryString;
  if (columns) {
    // filter out hidden columns
    columns = _.filter(columns, (v) => !v.flags?.includes('hidden'));
    // extract field name and create string
    queryString = _.map(columns, (v) => v.fieldName).join(', ');

    // get order by
    const orderByString = getNonSoqlOrderBy(view);

    return `SELECT ${queryString}${orderByString || ''}`;
  }
};

export const shouldRenderWarning = (
  downloadType: string | undefined,
  selectedFiltered: boolean,
  rowCountFiltered: number | undefined,
  rowCountAll: number | undefined
) => {
  if (FeatureFlags.valueOrDefault('enable_table_formatted_exports', false) === false) {
    return false;
  }
  const filteredOption = downloadType === 'xlsx' && selectedFiltered;
  const allOption = downloadType === 'xlsx' && !selectedFiltered;
  const filteredDataExceeded = rowCountFiltered !== undefined && rowCountFiltered > 5000;
  const allDataExceeded = rowCountAll !== undefined && rowCountAll > 5000;

  return (filteredOption && filteredDataExceeded) || (allOption && allDataExceeded);
};

// A helper function to determine whether the selected download option exceeds threshold.
export const shouldRenderLargeDatasetWarning = (
  selectedFiltered: boolean,
  rowCountFiltered: number | undefined,
  rowCountAll: number | undefined,
  threshold = 999999
) => {
  const filteredDataExceeded = rowCountFiltered !== undefined && rowCountFiltered > threshold;
  const allDataExceeded = rowCountAll !== undefined && rowCountAll > threshold;

  return (selectedFiltered && filteredDataExceeded) || (!selectedFiltered && allDataExceeded);
};
